import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftRight as ArrowLeftRightIcon,
  CircleX as CircleXIcon,
  RotateCcw as RotateCcwIcon,
  UserMinus as UserMinusIcon,
  UserPlus as UserPlusIcon,
  type LucideIcon,
} from "lucide-react";
import type { ConversationTimelineEventDto } from "@multi-chat/shared";
import { formatMessageTime } from "./inbox-display.js";

/**
 * Persisted ConversationEvent only — never synthesized (C-7).
 * Copy lives under inbox.workflow.event.*. Names are separate text nodes.
 */
export function ConversationSystemEventPill({
  event,
  locale,
}: {
  event: ConversationTimelineEventDto;
  locale: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const time = formatMessageTime(event.occurredAt, locale);
  const fullTime = formatEventFullTime(event.occurredAt, locale);
  const segments = eventSegments(event, t);
  const sentence = segments.map((segment) => segment.value).join(" ");
  const accessible = [sentence, fullTime].filter((part) => part.length > 0).join(" · ");
  const Icon = EVENT_ICONS[event.eventType];

  return (
    <div className="flex justify-center px-3">
      <div
        tabIndex={0}
        data-testid="conversation-system-event"
        data-event-type={event.eventType}
        data-icon={EVENT_ICON_NAMES[event.eventType]}
        aria-label={accessible}
        title={fullTime || undefined}
        className="my-[10px] inline-flex w-max min-w-0 max-w-[92%] items-center gap-1.5 rounded-full bg-inbox-pill px-[11px] py-[3px] text-center text-[0.71875rem] leading-[1.6] font-[450] text-[#4f625d] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <Icon className="size-3.5 shrink-0 text-[#6c7f7a]" strokeWidth={1.75} aria-hidden="true" />
        <span className="min-w-0 [overflow-wrap:anywhere]">
          {renderSegments(segments)}
          {time ? (
            <>
              <span aria-hidden="true"> · </span>
              <time dateTime={event.occurredAt} className="tabular-nums">
                {time}
              </time>
            </>
          ) : null}
        </span>
      </div>
    </div>
  );
}

const EVENT_ICONS = {
  ASSIGNED: UserPlusIcon,
  REASSIGNED: ArrowLeftRightIcon,
  UNASSIGNED: UserMinusIcon,
  CLOSED: CircleXIcon,
  REOPENED: RotateCcwIcon,
} as const satisfies Record<ConversationTimelineEventDto["eventType"], LucideIcon>;

const EVENT_ICON_NAMES = {
  ASSIGNED: "user-plus",
  REASSIGNED: "arrow-left-right",
  UNASSIGNED: "user-minus",
  CLOSED: "circle-x",
  REOPENED: "rotate-ccw",
} as const satisfies Record<ConversationTimelineEventDto["eventType"], string>;

type Segment = { kind: "text" | "name"; value: string };

function renderSegments(segments: Segment[]): ReactNode {
  return segments.map((segment, index) => {
    const leadingSpace = index > 0 ? " " : null;
    if (segment.kind === "name") {
      return (
        <Fragment key={`${segment.kind}-${index}`}>
          {leadingSpace}
          <strong className="font-semibold text-[#253b35]">
            <bdi>{segment.value}</bdi>
          </strong>
        </Fragment>
      );
    }
    return (
      <Fragment key={`${segment.kind}-${index}`}>
        {leadingSpace}
        <span>{segment.value}</span>
      </Fragment>
    );
  });
}

function eventSegments(
  event: ConversationTimelineEventDto,
  t: (key: string) => string,
): Segment[] {
  const actor = actorName(event, t);
  const assignee = event.toAssignedMember?.displayName ?? "";

  switch (event.eventType) {
    case "ASSIGNED":
      return [
        { kind: "text", value: t("inbox.workflow.event.assignedTo") },
        { kind: "name", value: assignee },
        { kind: "text", value: t("inbox.workflow.event.by") },
        { kind: "name", value: actor },
      ];
    case "REASSIGNED":
      return [
        { kind: "text", value: t("inbox.workflow.event.reassignedTo") },
        { kind: "name", value: assignee },
        { kind: "text", value: t("inbox.workflow.event.by") },
        { kind: "name", value: actor },
      ];
    case "UNASSIGNED":
      return event.reason === "MEMBER_REMOVED"
        ? [{ kind: "text", value: t("inbox.workflow.event.unassignedMemberRemoved") }]
        : [
            { kind: "text", value: t("inbox.workflow.event.unassignedBy") },
            { kind: "name", value: actor },
          ];
    case "CLOSED":
      return [
        { kind: "text", value: t("inbox.workflow.event.closedBy") },
        { kind: "name", value: actor },
      ];
    case "REOPENED":
      return event.reason === "CUSTOMER_INBOUND" || event.actorType === "SYSTEM"
        ? [{ kind: "text", value: t("inbox.workflow.event.reopenedCustomer") }]
        : [
            { kind: "text", value: t("inbox.workflow.event.reopenedBy") },
            { kind: "name", value: actor },
          ];
    default: {
      const _exhaustive: never = event.eventType;
      return _exhaustive;
    }
  }
}

function actorName(event: ConversationTimelineEventDto, t: (key: string) => string): string {
  if (event.actorType === "PLATFORM_ADMIN") {
    return t("inbox.workflow.event.feedbotTeam");
  }
  return event.actorLabel;
}

function formatEventFullTime(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
